import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Collapse,
    Divider,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    BarChart as BarChartIcon,
    LocalHospital as ClinicIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    SupportAgent as RepIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

// ─── Clinic List Item ─────────────────────────────────────────────────────────

const ClinicListItem = ({ clinic, t }) => {
    const repName =
        clinic.representative?.name ||
        clinic.representative?.username ||
        null;

    const cityState = [clinic.address?.city, clinic.address?.state]
        .filter(Boolean)
        .join(', ');

    return (
        <Box
            sx={{
                p: 1.5,
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
            }}
        >
            {/* Clinic name + active badge */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={0.5}
            >
                <Box display="flex" alignItems="center" gap={0.75}>
                    <ClinicIcon fontSize="small" color="primary" />
                    <Typography variant="body2" fontWeight="bold">
                        {clinic.name}
                    </Typography>
                </Box>
                {!clinic.isActive && (
                    <Chip
                        label={t('doctors.inactive', 'Inactive')}
                        size="small"
                        color="default"
                        sx={{ height: 18, fontSize: 10 }}
                    />
                )}
            </Box>

            {/* Phone */}
            {clinic.contactNumber && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                    <PhoneIcon
                        sx={{ fontSize: 13, color: 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {clinic.contactNumber}
                    </Typography>
                </Box>
            )}

            {/* Location */}
            {cityState && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                    <LocationIcon
                        sx={{ fontSize: 13, color: 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {cityState}
                    </Typography>
                </Box>
            )}

            {/* Representative */}
            {repName && (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <RepIcon
                        sx={{ fontSize: 13, color: 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {repName}
                    </Typography>
                </Box>
            )}

            {/* Notes */}
            {clinic.notes && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{
                        mt: 0.5,
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {clinic.notes}
                </Typography>
            )}
        </Box>
    );
};

// ─── Doctor Card ──────────────────────────────────────────────────────────────

const DoctorCard = ({ doctor, onEdit, onDelete, canEdit, canDelete }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();

    const [clinicsExpanded, setClinicsExpanded] = useState(false);

    const activeClinics = (doctor.clinics || []).filter((c) => c.isActive);
    const totalClinics = (doctor.clinics || []).length;
    const hasClinics = totalClinics > 0;

    return (
        <Card>
            <CardContent>
                {/* ── Doctor header ── */}
                <Box display="flex" alignItems="center" mb={2}>
                    <PersonIcon
                        sx={{ fontSize: 40, mr: 2, color: 'primary.main' }}
                    />
                    <Box flexGrow={1}>
                        <Typography variant="h6" component="h2">
                            {`${t('doctors.dr')} ${doctor.name}`}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <WorkIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="textSecondary">
                                {doctor?.specialization?.name}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* ── Status chips ── */}
                <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                    <Chip
                        label={
                            doctor.isActive
                                ? t('doctors.active')
                                : t('doctors.inactive')
                        }
                        color={doctor.isActive ? 'success' : 'default'}
                        size="small"
                    />
                    {hasClinics && (
                        <Chip
                            icon={<ClinicIcon sx={{ fontSize: '14px !important' }} />}
                            label={`${totalClinics} ${totalClinics === 1
                                ? t('doctors.clinics.clinic', 'Clinic')
                                : t('doctors.clinics.clinics', 'Clinics')
                                }`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => setClinicsExpanded((p) => !p)}
                            sx={{ cursor: 'pointer' }}
                        />
                    )}
                </Box>

                {/* ── Info table ── */}
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ mt: 2 }}
                >
                    <Table size="small">
                        <TableBody>
                            {doctor.licenseNumber && (
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        sx={{
                                            fontWeight: 'bold',
                                            width: '40%',
                                        }}
                                    >
                                        {t('doctors.license')}
                                    </TableCell>
                                    <TableCell>
                                        {doctor.licenseNumber}
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell
                                    component="th"
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {t('doctors.phone')}
                                </TableCell>
                                <TableCell>{doctor.contactNumber}</TableCell>
                            </TableRow>
                            {doctor.email && (
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('doctors.email')}
                                    </TableCell>
                                    <TableCell>{doctor.email}</TableCell>
                                </TableRow>
                            )}
                            {doctor.address?.city && (
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('doctors.address')}
                                    </TableCell>
                                    <TableCell>
                                        {[
                                            doctor.address.city,
                                            doctor.address.state,
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {/* Legacy top-level representative */}
                            {doctor.representative && (
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('representatives.title', 'Representative')}
                                    </TableCell>
                                    <TableCell>
                                        {doctor.representative?.name ||
                                            doctor.representative?.username ||
                                            '—'}
                                    </TableCell>
                                </TableRow>
                            )}
                            {typeof doctor.totalPatientsReferred ===
                                'number' && (
                                    <TableRow>
                                        <TableCell
                                            component="th"
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {t('doctors.patientsReferred')}
                                        </TableCell>
                                        <TableCell>
                                            {doctor.totalPatientsReferred}
                                        </TableCell>
                                    </TableRow>
                                )}
                            {typeof doctor.totalScansReferred === 'number' && (
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('doctors.scansReferred')}
                                    </TableCell>
                                    <TableCell>
                                        {doctor.totalScansReferred}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* ── Clinics collapsible section ── */}
                {hasClinics && (
                    <>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mt={2}
                            mb={0.5}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setClinicsExpanded((p) => !p)}
                        >
                            <Box display="flex" alignItems="center" gap={0.75}>
                                <ClinicIcon
                                    fontSize="small"
                                    color="primary"
                                />
                                <Typography
                                    variant="subtitle2"
                                    color="primary"
                                >
                                    {t('doctors.clinics.title', 'Clinics')}
                                    {activeClinics.length !== totalClinics && (
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ ml: 0.5 }}
                                        >
                                            ({activeClinics.length}{' '}
                                            {t('doctors.active', 'active')})
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>
                            <Tooltip
                                title={
                                    clinicsExpanded
                                        ? t('common.collapse', 'Collapse')
                                        : t('common.expand', 'Expand')
                                }
                            >
                                <IconButton size="small">
                                    {clinicsExpanded ? (
                                        <ExpandLessIcon fontSize="small" />
                                    ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Collapse in={clinicsExpanded} timeout="auto" unmountOnExit>
                            <Box pt={1}>
                                {doctor.clinics.map((clinic, idx) => (
                                    <ClinicListItem
                                        key={clinic._id || idx}
                                        clinic={clinic}
                                        t={t}
                                    />
                                ))}
                            </Box>
                        </Collapse>
                    </>
                )}
            </CardContent>

            {/* ── Actions ── */}
            <CardActions>
                {isSuperAdmin && (
                    <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        startIcon={<BarChartIcon />}
                        onClick={() =>
                            navigate(`/doctors/${doctor._id}/stats`)
                        }
                    >
                        {t('doctors.viewStats') || 'View Statistics'}
                    </Button>
                )}
                {canEdit && (
                    <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => onEdit(doctor)}
                    >
                        {t('common.edit')}
                    </Button>
                )}
                {canDelete && (
                    <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => onDelete(doctor)}
                    >
                        {t('common.delete')}
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};

export default DoctorCard;